import {
  Bell,
  Building2,
  Cable,
  CreditCard,
  KeyRound,
  Landmark,
  type LucideIcon,
  Settings2,
  Sparkles,
  UserRound,
} from "lucide-react";
import { ROUTES, routeUrl } from "@/app/routes";

/// One entry in the settings rail.
export interface SettingsNavItem {
  /// Stable id, used as the React key and by the active-section test.
  id: string;
  label: string;
  icon: LucideIcon;
  /// Resolved at render time, since workspace-scoped destinations need the
  /// active slug and account-scoped ones do not.
  href: string;
  /// Owner-only sections are hidden outright rather than shown and then
  /// refused: the shore already 403s them, and a rail entry that always
  /// dead-ends is the bug the billing page's own IMPORTANT 1 fixed.
  ownerOnly?: boolean;
}

export interface SettingsNavGroup {
  heading: string;
  items: SettingsNavItem[];
}

/// Builds the rail.
///
/// Account sections are real children of `/settings`. Workspace sections
/// deliberately are NOT: they keep their existing `/<slug>/...` paths,
/// because which workspace they act on is carried by the slug, and that slug
/// is this app's source of truth for workspace context (see router.tsx's own
/// comment on the workspace layout). Folding them under the account-level
/// `/settings` would have meant inferring the workspace from ambient state
/// instead, which is exactly how you end up editing the wrong workspace's
/// settings after switching. They appear in the same rail, so the user sees
/// one settings surface; only the URL keeps the distinction.
export function buildSettingsNav({
  workspaceSlug,
  workspaceName,
  isOwner,
  canEditCompany = false,
  canManageAiConnections = false,
}: {
  workspaceSlug: string;
  workspaceName: string;
  isOwner: boolean;
  canEditCompany?: boolean;
  canManageAiConnections?: boolean;
}): SettingsNavGroup[] {
  const groups: SettingsNavGroup[] = [
    {
      heading: "Account",
      items: [
        { id: "profile", label: "Profile", icon: UserRound, href: ROUTES.settingsProfile.path },
        { id: "tokens", label: "API tokens", icon: KeyRound, href: ROUTES.settingsTokens.path },
        { id: "mcp", label: "MCP", icon: Sparkles, href: ROUTES.settingsMcp.path },
        ...(canManageAiConnections
          ? ([
              {
                id: "ai-connections",
                label: "MCP connections",
                icon: Cable,
                href: ROUTES.settingsAiConnections.path,
              },
            ] satisfies SettingsNavItem[])
          : []),
        { id: "account", label: "Account", icon: Settings2, href: ROUTES.settingsAccount.path },
        {
          id: "billing",
          label: "Billing",
          icon: CreditCard,
          href: ROUTES.settingsBilling.path,
        },
      ],
    },
    {
      heading: workspaceName,
      items: [
        {
          id: "workspace",
          label: "Workspace",
          icon: Building2,
          href: routeUrl.workspace.settings(workspaceSlug),
        },
        ...(canEditCompany
          ? ([
              {
                id: "company",
                label: "Company",
                icon: Landmark,
                href: routeUrl.workspace.settingsCompany(workspaceSlug),
              },
            ] satisfies SettingsNavItem[])
          : []),
        {
          id: "notifications",
          label: "Notifications",
          icon: Bell,
          href: `/${workspaceSlug}/notifications/preferences`,
        },
      ],
    },
  ];

  return groups
    .map((group) => ({ ...group, items: group.items.filter((i) => !i.ownerOnly || isOwner) }))
    .filter((group) => group.items.length > 0);
}

/// Which rail entry should read as active for a pathname. Longest matching
/// href wins, so `/settings/billing` does not also light up a `/settings`
/// entry, and a workspace sub-path lights its own section.
export function activeSettingsId(pathname: string, groups: SettingsNavGroup[]): string | null {
  const candidates = groups
    .flatMap((group) => group.items)
    .filter((item) => pathname === item.href || pathname.startsWith(`${item.href}/`))
    .sort((a, b) => b.href.length - a.href.length);
  return candidates[0]?.id ?? null;
}
